// Global hierarchical object containing sponsor-/vendor-/test-specific strings.
// The structure deliberately allows “holes”.  At any level you can specify a
// `default` block that will be used when a more-specific key is missing.

/*
 * Key order: STEP_TEXT[ttGroup][ehrVendor][testType][stepId][timeoutVariant]
 *   ttGroup       – LF, VA, etc.  Use 'default' as a catch-all.
 *   ehrVendor     – e.g., '1' (VistA/CPRS), '2' (Federal EHR).  Use 'default'.
 *   testType      – inpatient, ambulatory, etc.  Use 'default'.
 *   stepId        – logical identifier inside the app (step2, step4, timeoutCard…)
 *   timeoutVariant – 'timeoutsOn' | 'timeoutsOff'
 *
 * Each leaf is **a single HTML string** that contains the entire block to be
 * rendered (paragraph + <ul>).  Historically we stored arrays of individual
 * bullet lines, but product preference is to manage the whole section in one
 * place so copywriters can edit more easily.  Components inject it via
 * `dangerouslySetInnerHTML`.
 * Strings may contain simple placeholders enclosed in curly braces, such as
 *     {deadline}
 * Components perform the value substitution themselves.
 */

export const STEP_TEXT = {
  // ------------------------ VA ------------------------ //
  VA: {
    // ----- Federal EHR / Cerner ----- //
    2: {
      inpatient: {
        step2: {
          timeoutsOn:
              `<p class="mt-2"><strong>Note:</strong> Oracle Health has entered the test patients in your healthcare system’s EHR
                               <br />To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> by confirming that you have access to the Test Patients by opening and reviewing the patient profiles by using Federal patient identifiers provided in the PDF and continue to <strong>Step 3</strong> by <strong>{deadline} EDT</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong> within <strong>3 hours</strong>.
               </li>
             </ul>`,
          timeoutsOff:
              `<p class="mt-2"><strong>Note:</strong> Oracle Health has entered the test patients in your healthcare system’s EHR
                               <br />To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> by confirming that you have access to the Test Patients by opening and reviewing the patient profiles by using Federal patient identifiers provided in the PDF and continue to <strong>Step 3</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong> without stopping.
               </li>
             </ul>`
        }
      },
      // Generic vendor fallback (used when the test type is not yet
      // explicitly defined for vendor "2")
      default: {
        default: {
          step2: {
            timeoutsOn:
              `<p class="mt-2">To complete the test, you will need to:</p>
               <ul>
                 <li>
                   Complete <strong>Step 2</strong> and continue to Step 3 by <strong>{deadline} EDT</strong>.
                 </li>
                 <li>
                   Then complete Steps 3–6 within <strong>3 hours</strong>.
                 </li>
               </ul>`,
            timeoutsOff:
              `<p class="mt-2">To complete the test, you will need to:</p>
               <ul>
                 <li>
                   Complete <strong>Step 2</strong> and proceed to Step 3.
                 </li>
                 <li>
                   Then complete Steps 3–6.
                 </li>
               </ul>`
          }
        }
      }
    },

    // ----- VistA / CPRS (Legacy) – vendor code "1" ----- //
    1: {
      inpatient: {
        step2: {
          timeoutsOn:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> by confirming that you have entered the Test Patients into your hospital system(s) and continue to  <strong>Step 3</strong> by <strong>{deadline} EDT</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong> within <strong>3 hours</strong>.
               </li>
             </ul>`,
          timeoutsOff:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> by confirming that you have entered the Test Patients into your hospital system(s) and continue to  <strong>Step 3</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong>.
               </li>
             </ul>`
        }
      },
      default: {
        default: {
          step2: {
            timeoutsOn:
              `<p class="mt-2">To complete the test, you will need to:</p>
               <ul>
                 <li>
                   Complete <strong>Step 2</strong> and continue to Step 3 by <strong>{deadline} EDT</strong>.
                 </li>
                 <li>
                   Then complete Steps 3–6 within <strong>3 hours</strong>.
                 </li>
               </ul>`,
            timeoutsOff:
              `<p class="mt-2">To complete the test, you will need to:</p>
               <ul>
                 <li>
                   Complete <strong>Step 2</strong> and proceed to Step 3.
                 </li>
                 <li>
                   Then complete Steps 3–6.
                 </li>
               </ul>`
          }
        }
      }
    },

    // default vendor block for VA (if an unknown vendor code comes in)
    default: {
      inpatient: {
        step2: {
          timeoutsOn:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> by confirming the test patients are entered and continue to Step 3 by <strong>{deadline} EDT</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong> within <strong>3 hours</strong>.
               </li>
             </ul>`,
          timeoutsOff:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Confirm the patients are entered and proceed to <strong>Step 3</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong>.
               </li>
             </ul>`
        }
      }
    }
  },

  // ---------------------- Leapfrog -------------------- //
  LF: {
    default: {
      inpatient: {
        step2: {
          timeoutsOn:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> by confirming that you have entered the Test Patients into your hospital system(s) and continue to <strong>Step 3</strong> by <strong>{deadline} EDT</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong> within <strong>3 hours</strong>.
               </li>
             </ul>`,
          timeoutsOff:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> by confirming that you have entered the Test Patients into your hospital system(s) and proceed to <strong>Step 3</strong>.
               </li>
               <li>
                 Then complete <strong>Steps 3–6</strong>.
               </li>
             </ul>`
        }
      }
    }
  },

  // ---------------------- Fallback -------------------- //
  default: {
    default: {
      default: {
        step2: {
          timeoutsOn:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> and continue to Step 3 by <strong>{deadline}</strong>.
               </li>
               <li>
                 Then complete the subsequent steps within the allocated time.
               </li>
             </ul>`,
          timeoutsOff:
            `<p class="mt-2">To complete the test, you will need to:</p>
             <ul>
               <li>
                 Complete <strong>Step 2</strong> and proceed to Step 3.
               </li>
               <li>
                 Then complete the remaining steps.
               </li>
             </ul>`
        }
      }
    }
  }
};

// NOTE: Only the strings we *currently* need are populated.  As pages are
// touched, copy for additional steps / tests / orgs should be appended here –
// do NOT hard-code new strings in JSX.
